import { getRiskBandsMode } from "@/features/branding";
import { useCollectionTutorialsState, type PointDetailsPermissions } from "@/features/collection_home";
import type { HazardPlusTotal, ReturnPeriod, Year } from "@/features/pe_aldd";
import { Rcp } from "@/types/rcp";
import { returnPeriods } from "@/types/returnPeriod";
import { getAnalysisTypeName } from "@/utils/string";
import CompareModeSelector from "../../collection_home/components/CompareModeSelector";
import SidebarPointAdaptationDetails from "../../collection_home/components/SidebarPointAdaptationDetails";
import type { PathwayWithActions } from "../../collection_home/hooks/useAutoAdaptationPathways";
import type { CompareResultsMode } from "../../collection_home/types";
import type { CompareSegment } from "../../collection_home/utils/compareModeState";
import { PointDetailsPopup } from "../components/PointDetailsPopup";
import type { AnalysisPointDetailTab, MapDisplayPoint, RiskiestSettings } from "../types";
import { PointRiskDetails } from "./PointRiskDetails";
import { SidebarPointTabPanel } from "./SidebarPointTabPanel";
export { SidebarPointTabPanel };
export const SidebarPointDetails = ({
  riskiestSettings,
  isRiskyPoint,
  point,
  limitYears,
  activeTab = "risk",
  resultsMode = "baseline",
  onCompareResultsModeChange = () => null,
  canRunAutoAdaptation = false,
  hasAutoAdaptations = false,
  compareSegments = [],
  displayReturnPeriod,
  year,
  rcp,
  hazard,
  isLargeSite,
  permissions,
  regionType,
  pathways,
  adaptationLoadingAssetId,
  highLightedPathwayId,
  setHighLightedPathwayId = () => null,
  adaptationError = false,
  retryAdaptations = () => null,
  onBack = () => null,
  updateAvailable
}: {
  activeTab: AnalysisPointDetailTab;
  resultsMode?: CompareResultsMode;
  onCompareResultsModeChange?: (mode: CompareResultsMode) => void;
  canRunAutoAdaptation?: boolean;
  hasAutoAdaptations?: boolean;
  /** Built by `buildPointCompareSegments`. */
  compareSegments?: CompareSegment[];
  riskiestSettings: RiskiestSettings | undefined;
  isRiskyPoint: boolean;
  point: MapDisplayPoint;
  limitYears: number[];
  displayReturnPeriod?: ReturnPeriod;
  year: Year;
  rcp: Rcp;
  hazard: HazardPlusTotal;
  isLargeSite: boolean;
  permissions: PointDetailsPermissions;
  regionType?: string;
  pathways?: PathwayWithActions[];
  adaptationLoadingAssetId?: string | null;
  highLightedPathwayId?: string | null;
  setHighLightedPathwayId?: (id: string | null) => void;
  adaptationError?: boolean;
  retryAdaptations?: () => void;
  onBack?: () => void;
  updateAvailable: boolean;
}) => {
  const riskBandsMode = getRiskBandsMode();
  const displayName = point.displayInput || point.input || point.name;
  const backText = `Back to all ${getAnalysisTypeName(isLargeSite, true).toLowerCase()}`;
  const {
    bindPointCompareToggle
  } = useCollectionTutorialsState();
  return <div className="animate-fade-in-slide flex h-full flex-col @container" data-sentry-component="SidebarPointDetails" data-sentry-source-file="SidebarPointDetails.tsx">
      <div className="flex flex-col justify-center gap-12 pb-16 @container/pointLevelWrapper">
        {activeTab === "exposure" && <SidebarPointTabPanel onBack={onBack} backText={backText} title={displayName}>
            <PointDetailsPopup activeTab={activeTab} displayReturnPeriod={displayReturnPeriod} point={point} riskiestSettings={riskiestSettings} isRiskyPoint={isRiskyPoint} displayYear={year} limitYears={limitYears} limitReturnPeriods={returnPeriods} riskBandsMode={riskBandsMode} fullWidth hideTitle />
          </SidebarPointTabPanel>}

        {activeTab === "risk" && <SidebarPointTabPanel onBack={onBack} backText={backText} title={displayName} extra={!isLargeSite && updateAvailable && <div ref={bindPointCompareToggle}>
                  <CompareModeSelector size="small" value={resultsMode} onChange={onCompareResultsModeChange} options={compareSegments} />
                </div>}>
            <PointRiskDetails point={point} resultsMode={resultsMode} canRunAutoAdaptation={canRunAutoAdaptation} hasAutoAdaptations={hasAutoAdaptations} isRiskyPoint={isRiskyPoint} year={year} rcp={rcp} hazard={hazard} isLargeSite={isLargeSite} permissions={permissions} regionType={regionType} pathways={pathways} adaptationLoadingAssetId={adaptationLoadingAssetId} />
          </SidebarPointTabPanel>}

        {activeTab === "adaptation" && canRunAutoAdaptation && <SidebarPointTabPanel onBack={onBack} backText={backText} title={displayName}>
            <SidebarPointAdaptationDetails regionType={regionType} point={point} year={year} rcp={rcp} hazard={hazard} pathways={pathways ?? []} adaptationLoadingAssetId={adaptationLoadingAssetId ?? null} highLightedPathwayId={highLightedPathwayId ?? null} setHighLightedPathwayId={setHighLightedPathwayId} permissions={permissions} isLargeSite={isLargeSite} adaptationError={adaptationError} retryAdaptations={retryAdaptations} />
          </SidebarPointTabPanel>}
      </div>
    </div>;
};